import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
const TAB_LABELS = {
  overview: "Overview", enquiries: "Enquiries", inquiries: "Inquiries", quotes: "Quotes", "service-requests": "Service", applications: "Applications",
  "career-apps": "Career Apps", people: "HR & Payroll", team: "Team", letters: "Appointment Letters", idcards: "ID Cards", payments: "Payments",
  products: "Store", signature: "Signature", "email-setup": "Email Setup", holidays: "Holidays", leave: "Leave",
};

const Check = ({ checked, onChange, label, testId }) => (
  <label className={`flex items-center gap-2.5 text-sm border px-3 py-2 cursor-pointer select-none transition-colors ${checked ? "border-nx-gold/40 text-slate-100 bg-nx-gold/[0.05]" : "border-white/10 text-slate-500"}`}>
    <input type="checkbox" className="accent-[#d4af5e]" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
    {label}
  </label>
);

export const AccessModal = ({ member, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState(null);
  const [portal, setPortal] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/module-access").then(({ data }) => {
      setCatalog(data);
      const doc = data.access[member.email];
      setPortal(doc ? doc.portal_modules : (data.role_defaults?.[member.role] || data.portal_modules.map((m) => m.key)));
      setHidden(doc ? doc.hidden_sections : []);
    }).catch((err) => { toast.error(err?.response?.data?.detail || "Could not load access settings"); onClose(); });
  }, [member.email, onClose]);
  const roleTabs = catalog?.role_sections?.[member.role] || [];
  const togglePortal = (key, on) => setPortal((p) => on ? [...p, key] : p.filter((k) => k !== key));
  const toggleTab = (key, on) => setHidden((h) => on ? h.filter((k) => k !== key) : [...h, key]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/module-access/${encodeURIComponent(member.email)}`, { portal_modules: portal, hidden_sections: hidden });
      toast.success(`Access updated for ${member.name}`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save access");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="access-modal">
      <div className="bg-nx-navy border border-white/15 w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><ShieldCheck size={13} className="text-nx-goldlight" /> Module access</p>
            <p className="text-white font-medium mt-1 truncate">{member.name} <span className="text-slate-500 font-normal">· {member.email}</span></p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none px-1" aria-label="Close" data-testid="access-modal-close">×</button>
        </div>
        {!catalog ? <p className="px-5 py-8 text-sm text-slate-500">Loading…</p> : (
          <div className="overflow-y-auto px-5 py-4 flex flex-col gap-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">People portal (erp.nexsecure.in/people) — left panel</p>
                <div className="flex gap-3 text-[10px] tracking-[0.12em] uppercase">
                  <button onClick={() => setPortal(catalog.portal_modules.map((m) => m.key))} className="text-nx-goldlight hover:underline" data-testid="access-portal-all">All</button>
                  <button onClick={() => setPortal(["home"])} className="text-slate-400 hover:underline" data-testid="access-portal-home-only">Home only</button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {catalog.portal_modules.map((m) => <Check key={m.key} label={m.label} checked={portal.includes(m.key)} onChange={(on) => togglePortal(m.key, on)} testId={`access-portal-${m.key}`} />)}
              </div>
            </div>
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-3">Team Tracker tabs (granted by role: {member.role})</p>
              {roleTabs.length === 0 ? <p className="text-xs text-slate-500">This role has no Team Tracker tabs.</p> : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {roleTabs.map((k) => <Check key={k} label={TAB_LABELS[k] || k} checked={!hidden.includes(k)} onChange={(on) => toggleTab(k, on)} testId={`access-tab-${k}`} />)}
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-600">Unticked items stay visible but greyed out; the staff member sees “Your role is not specified for this section. Please contact HR.” The backend enforces the same list.</p>
          </div>
        )}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="text-xs tracking-[0.14em] uppercase text-slate-400 hover:text-white px-4 py-2.5" data-testid="access-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !catalog} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-2.5 transition-colors disabled:opacity-60" data-testid="access-save">{saving ? "Saving…" : "Save access"}</button>
        </div>
      </div>
    </div>
  );
};
